import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["childMarginInlineStart", "children", "childTruncateLength", "displayName", "href", "isEditable", "lookup", "onClick", "showTooltip", "size", "socialNetwork", "tooltipPlacement", "tooltipTitle", "type"];
import { Component } from 'react';
import shallowCompare from 'react-addons-shallow-compare';
import { isTrellisTheme, useTheme } from 'foundations-theming/utils';
import { getAvatarResolver } from './lib/AvatarResolverWithSource';
import defaultFetchAvatars from './api/defaultFetchAvatars';
import { AVATAR_SIZES, DEFAULT_PROPS } from './Constants';
import { getAvatarSrc } from './lib/getAvatarSrc';
import defaultPortalId from './lib/defaultPortalId';
import { fetchImage, isFetched } from './lib/Image';
import LookupRecord from './Records/LookupRecord';
import { AvatarDisplay } from './AvatarDisplay';
import { getMarginProps } from './lib/stylePropHelpers';
import { getBorderProps } from './lib/stylePropHelpers';
import { AVATAR_SOURCES } from './lib/AvatarSources';
import { jsx as _jsx } from "react/jsx-runtime";
const DEFAULT_LOOKUP_RECORD = new LookupRecord();
export class AvatarRequestInternal extends Component {
  // Resolves the current avatar URI and load state from the resolver + image
  // caches for the given props. Returns null for lookup types that never have
  // avatars (deal, ticket, empty identifier). Accepts the current lookupRecord
  // so the equals-check optimisation in updateAvatarUrl can reuse the same
  // instance when props haven't changed.
  static resolveCacheState(props, existingLookupRecord) {
    const {
      lookup
    } = props;
    if (!lookup.primaryIdentifier || !lookup.type || typeof lookup.primaryIdentifier === 'string' && lookup.primaryIdentifier.length < 1 || 'deal' === lookup.type || 'ticket' === lookup.type) {
      return null;
    }
    const sizePx = props.size !== 'responsive' ? AVATAR_SIZES[props.size] : AVATAR_SIZES.xl;
    const lookupRecordFromProps = new LookupRecord(Object.assign({}, lookup, {
      portalId: props.portalId,
      dimensions: sizePx
    }));
    const lookupRecord = existingLookupRecord.equals(lookupRecordFromProps) ? existingLookupRecord : lookupRecordFromProps;
    const {
      uri: avatarURI,
      source
    } = getAvatarResolver().getAvatarCacheEntry(lookupRecord, props.fetchAvatars, props.portalId, props.trellisDefault);
    const isDefault = typeof avatarURI !== 'string';
    const loaded = isDefault || !!isFetched(avatarURI);
    return {
      avatarURI,
      source,
      loaded,
      lookupRecord
    };
  }

  // Synchronously reads from the resolver + image cache to compute initial state.
  // Only uses cached values when fully loaded (resolver has the URI AND the image
  // is already in the browser cache). If either cache is cold, returns blank state
  // so componentDidMount's updateAvatarUrl handles the full fetch path normally.
  static computeStateFromProps(props) {
    const blankState = {
      lookupRecord: DEFAULT_LOOKUP_RECORD,
      avatarURI: undefined,
      source: null,
      error: undefined,
      loaded: false
    };
    const resolved = AvatarRequestInternal.resolveCacheState(props, DEFAULT_LOOKUP_RECORD);
    if (resolved === null) {
      return Object.assign({}, blankState, {
        loaded: true,
        source: AVATAR_SOURCES.UNKNOWN
      });
    }
    if (!resolved.loaded) {
      return blankState;
    }
    return Object.assign({}, resolved, {
      error: undefined
    });
  }
  constructor(_props) {
    super(_props);
    this._isMounted = false;
    this.handleResolverUpdate = () => {
      this.updateAvatarUrl(this.props);
    };
    this.startWatching = () => {
      getAvatarResolver().watch(this.handleResolverUpdate);
    };
    this.stopWatching = () => {
      getAvatarResolver().unwatch(this.handleResolverUpdate);
    };
    this.updateAvatarUrl = props => {
      const resolved = AvatarRequestInternal.resolveCacheState(props, this.state.lookupRecord);
      const nextAvatarURI = resolved !== null ? resolved.avatarURI : undefined;
      if (this.state.avatarURI === nextAvatarURI) {
        return null;
      }
      if (resolved === null) {
        this.setState({
          loaded: true,
          avatarURI: undefined,
          source: AVATAR_SOURCES.UNKNOWN
        });
        return undefined;
      }
      const {
        avatarURI,
        source,
        loaded,
        lookupRecord
      } = resolved;
      this.setState({
        avatarURI,
        lookupRecord,
        error: undefined,
        loaded,
        source
      });

      // Forces the browser to load the image into the cache to avoid UI-jitters
      // as the image loads in.
      if (!loaded && typeof avatarURI === 'string') {
        fetchImage(avatarURI).then(() => {
          if (this._isMounted && this.state.avatarURI === avatarURI) {
            this.setState({
              loaded: true
            });
          }
        }).catch(error => {
          if (this._isMounted && this.state.avatarURI === avatarURI) {
            this.setState({
              error
            });
          }
        });
      }
      return null;
    };
    this.state = AvatarRequestInternal.computeStateFromProps(_props);
  }
  componentDidMount() {
    this._isMounted = true;
    this.startWatching();
    this.updateAvatarUrl(this.props);
  }
  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }
  componentDidUpdate() {
    this.updateAvatarUrl(this.props);
  }
  componentWillUnmount() {
    this._isMounted = false;
    this.stopWatching();
  }
  render() {
    const _this$props = this.props,
      {
        childMarginInlineStart,
        children,
        childTruncateLength,
        displayName,
        href,
        isEditable,
        lookup,
        onClick,
        showTooltip,
        size,
        socialNetwork,
        tooltipPlacement,
        tooltipTitle,
        type
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const borderProps = getBorderProps(rest);
    const marginProps = getMarginProps(rest);
    const {
      src,
      source
    } = getAvatarSrc({
      avatarURI: this.state.avatarURI,
      error: this.state.error,
      loaded: this.state.loaded,
      src: this.props.src,
      source: this.state.source
    });
    const isLoaded = this.state.loaded && (!!this.state.source || !!this.props.src);
    return /*#__PURE__*/_jsx(AvatarDisplay, Object.assign({}, Object.assign({
      'data-test-id': this.props['data-test-id'],
      childMarginInlineStart,
      children,
      childTruncateLength,
      displayName,
      href,
      isEditable,
      onClick,
      showTooltip,
      size,
      socialNetwork,
      src,
      tooltipPlacement,
      tooltipTitle,
      type: type || lookup.type,
      isLoaded,
      source
    }, borderProps, marginProps)));
  }
}
AvatarRequestInternal.defaultProps = Object.assign({}, DEFAULT_PROPS, {
  portalId: defaultPortalId(),
  fetchAvatars: defaultFetchAvatars
});
export function AvatarRequest(props) {
  const theme = useTheme();
  const trellisDefault = isTrellisTheme(theme);
  return /*#__PURE__*/_jsx(AvatarRequestInternal, Object.assign({}, props, {
    trellisDefault: trellisDefault
  }));
}